import { S } from './state.js';
import { ctx } from './canvas.js';
import { applyBurn, applyPoison, damageEnemy, hurtPlayer } from './combat.js';
import { BAL } from './config.js';
import { rnd, rrange } from './fx.js';
import { cam } from './meter.js';
import { player, threatDmgMul } from './entities.js';
// ---- Hazards (void pools that damage you if you stand in them) ----
export function updateHazards() {
  if (S.waveNum >= 4 && --S.hazardTimer <= 0) {
    if (S.hazards.length < 3) {
      const ang = rrange(0, Math.PI * 2), rad = rrange(140, 380);
      S.hazards.push({ x: player.x + Math.cos(ang) * rad, y: player.y + Math.sin(ang) * rad,
                     r: rrange(48, 88), t: 70, life: rrange(380, 620) });
    }
    S.hazardTimer = Math.floor(rrange(320, 580));
  }
  for (const h of S.hazards) {
    if (h.t > 0) { h.t--; continue; }   // still telegraphing in — harmless
    h.life--;
    // pool chip rides the shared damage curve but is CAPPED vs your bar (0.17): at deep waves the raw
    // tick outgrows any HP stack (wave 45 ≈ 100/tick ≈ 450 dps) and "stood in a puddle" became the
    // death sentence the burst ceiling was meant to prevent. Stacking max HP now IS hazard resistance.
    if (Math.hypot(h.x - player.x, h.y - player.y) < h.r + player.r)
      hurtPlayer(Math.min((6 + S.waveNum * 0.3) * threatDmgMul(), S.MAX_HP * BAL.diff.hazardCapPct), null, 'hazard pool');
  }
  S.hazards = S.hazards.filter(h => h.life > 0 && Math.hypot(h.x - player.x, h.y - player.y) < 2200);
}
export function drawHazards() {
  for (const h of S.hazards) {
    const x = h.x - cam.x, y = h.y - cam.y;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    if (h.t > 0) {                                   // telegraph: a shrinking warning ring
      const p = 1 - h.t / 70;
      ctx.globalAlpha = 0.3 + 0.3 * Math.abs(Math.sin(S.frame * 0.3));
      ctx.strokeStyle = '#ff4a7a'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, h.r * (0.3 + 0.7 * p), 0, Math.PI * 2); ctx.stroke();
      ctx.globalAlpha = 1; continue;
    }
    const fade = Math.min(1, h.life / 60);
    const g = ctx.createRadialGradient(x, y, 0, x, y, h.r);
    g.addColorStop(0, `rgba(140,20,70,${0.55 * fade})`);
    g.addColorStop(0.7, `rgba(80,10,40,${0.35 * fade})`);
    g.addColorStop(1, 'rgba(80,10,40,0)');
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, h.r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = `rgba(255,80,120,${0.5 * fade})`; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, h.r * (0.72 + 0.08 * Math.sin(S.frame * 0.12)), 0, Math.PI * 2); ctx.stroke();
  }
  ctx.globalAlpha = 1;
}
// ---- Ground patches: Wall of Fire (ascended Scattergun) + Scorch dash trails + Miasma toxic pools ----
// kinds: undefined/'fire' = burning ground (ignites; Scorch patches also tick direct damage with their
// own meter row) · 'toxic' = Miasma pool (stacks poison + ticks a %-max-HP bite, src 'miasma')
export function updateGroundFires() {
  if (!S.groundFires.length) return;
  for (const g of S.groundFires) {
    g.life--;
    if (S.frame % 6 === 0) {
      for (const e of S.enemies) {
        if (e.dead || Math.hypot(e.x - g.x, e.y - g.y) >= g.r + e.r) continue;
        if (g.kind === 'toxic') {
          damageEnemy(e, S.DAMAGE * 0.06 + e.maxHp * 0.0015 * S.biteScale, 1, 'miasma');   // ~60% dmg + 1.5% maxHP per second
          if (!e.dead && S.frame % 30 === 0) applyPoison(e, 120);            // lingering in the pool stacks poison
        } else {
          applyBurn(e, S.BURN_DPS * 0.8 * (1 + 0.2 * (S.items.magmacore || 0)), 36);   // Magma Core: hotter ground (0.18)
          if (g.src && !e.dead) damageEnemy(e, (S.DAMAGE * 0.1 + e.maxHp * 0.002 * (e.kind === 'boss' ? 0.25 : 1) * S.biteScale) * (1 + 0.2 * (S.items.magmacore || 0)), 1, g.src);    // Scorch: direct ~100% dmg/s + 2%maxHP/s (0.19: ground control is fire's second endgame path — flat ticks decayed deep)
        }
      }
    }
  }
  S.groundFires = S.groundFires.filter(g => g.life > 0);
}
export function drawGroundFires() {
  for (const g of S.groundFires) {
    const x = g.x - cam.x, y = g.y - cam.y;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    const fade = Math.min(1, g.life / 40);
    const toxic = g.kind === 'toxic';
    const gr = ctx.createRadialGradient(x, y, 0, x, y, g.r);
    gr.addColorStop(0, toxic ? `rgba(120,224,80,${0.32 * fade})` : `rgba(255,140,40,${0.4 * fade})`);
    gr.addColorStop(1, toxic ? 'rgba(60,160,40,0)' : 'rgba(255,80,20,0)');
    ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(x, y, g.r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = toxic ? `rgba(140,230,100,${(0.28 + 0.18 * Math.sin(S.frame * 0.2 + g.x)) * fade})`
                            : `rgba(255,170,70,${(0.3 + 0.2 * Math.sin(S.frame * 0.3 + g.x)) * fade})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(x, y, g.r * 0.8, 0, Math.PI * 2); ctx.stroke();
  }
}
export function drawMortars() {
  for (const m of S.mortars) {
    const x = m.x - cam.x, y = m.y - cam.y;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    if (m.t0 == null) m.t0 = Math.max(1, m.t);       // capture this shell's fuse length — bosses lob VARIED fuses (creep/airburst > 52), so a fixed /52 went negative → "arc: Negative radius" crash
    const p = Math.max(0, Math.min(1, 1 - m.t / m.t0));   // 0→1 as impact nears; clamped so any fuse stays valid
    ctx.strokeStyle = `rgba(255,90,90,${0.4 + 0.45 * p})`; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, Math.max(0, m.r), 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = `rgba(255,80,80,${0.1 + 0.2 * p})`;
    ctx.beginPath(); ctx.arc(x, y, Math.max(0, m.r * p), 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}
export function drawArena() {                                   // the boss-fight boundary ring + danger glow
  if (!S.arena.active) return;
  const x = S.arena.x - cam.x, y = S.arena.y - cam.y;
  const grd = ctx.createRadialGradient(x, y, S.arena.r * 0.82, x, y, S.arena.r + 50);
  grd.addColorStop(0, 'rgba(255,80,80,0)');
  grd.addColorStop(1, 'rgba(255,60,60,0.20)');
  ctx.fillStyle = grd; ctx.beginPath(); ctx.arc(x, y, S.arena.r + 50, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = 'rgba(255,90,90,0.6)'; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.arc(x, y, S.arena.r, 0, Math.PI * 2); ctx.stroke();
  ctx.strokeStyle = `rgba(255,150,90,${0.18 + 0.14 * Math.sin(S.frame * 0.06)})`; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(x, y, S.arena.r - 7, 0, Math.PI * 2); ctx.stroke();
}
// ---- The map (0.16): blob boundary + rock formations, re-rolled every run ----
// The boundary radius is WORLD_R modulated by three sine harmonics → an organic shape, not a circle.
// Rocks block MOVEMENT (bodies slide along them) but never bullets/blasts — low debris you shoot over.
// That one rule keeps every weapon working while the terrain carves lanes, chokepoints and cover-chases.
S.worldSeed = { p1: 0, p2: 0, p3: 0 };   // boundary harmonic phases (rolled in generateMap)
S.obstacles = [];                         // rocks: {x, y, r, verts:[{a, m}]} — verts = render jitter only
export function worldLimA(a) {
  return S.WORLD_R * (1 + 0.10 * Math.sin(3 * a + S.worldSeed.p1) + 0.07 * Math.sin(5 * a + S.worldSeed.p2) + 0.05 * Math.sin(8 * a + S.worldSeed.p3));
}
export function worldLim(x, y) { return worldLimA(Math.atan2(y, x)); }
export function clampToWorld(o, rad) {             // radial clamp to the blob boundary; true if it had to clamp
  const d = Math.hypot(o.x, o.y), lim = worldLim(o.x, o.y) - rad;
  if (d > lim) { o.x = o.x / d * lim; o.y = o.y / d * lim; return true; }
  return false;
}
export function pushOutOfRocks(o, rad) {           // circle slide for any {x, y} body
  for (const k of S.obstacles) {
    const dx = o.x - k.x, dy = o.y - k.y, d = Math.hypot(dx, dy) || 0.001, min = k.r + rad;
    if (d < min) { o.x = k.x + dx / d * min; o.y = k.y + dy / d * min; }
  }
}
export function generateMap() {
  S.worldSeed = { p1: rrange(0, 6.28), p2: rrange(0, 6.28), p3: rrange(0, 6.28) };
  S.obstacles = [];
  const place = (x, y, r) => {
    if (Math.hypot(x, y) < 340 + r) return;                       // the spawn point stays open
    if (Math.hypot(x, y) > worldLim(x, y) * 0.92 - r) return;     // not buried in the wall
    S.obstacles.push({ x, y, r, verts: Array.from({ length: 9 }, (_, i) =>
      ({ a: i / 9 * 6.28 + rrange(-0.12, 0.12), m: rrange(0.78, 1.12) })) });
  };
  for (let i = 0; i < 14; i++) {                                  // lone boulders
    const a = rrange(0, 6.28), d = rrange(0.18, 0.85) * S.WORLD_R;
    place(Math.cos(a) * d, Math.sin(a) * d, rrange(34, 72));
  }
  for (let w = 0; w < 7; w++) {                                   // rock walls: chains in a line or gentle arc
    const a = rrange(0, 6.28), d = rrange(0.25, 0.8) * S.WORLD_R;
    let x = Math.cos(a) * d, y = Math.sin(a) * d, dir = rrange(0, 6.28);
    const bend = rrange(-0.18, 0.18), n = 4 + Math.floor(rnd() * 4);
    for (let i = 0; i < n; i++) {
      const r = rrange(26, 40);
      place(x, y, r);
      x += Math.cos(dir) * r * 1.7; y += Math.sin(dir) * r * 1.7; dir += bend;
    }
  }
}
export function drawRocks() {
  for (const k of S.obstacles) {
    const x = k.x - cam.x, y = k.y - cam.y;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    ctx.beginPath();
    k.verts.forEach((v, i) => {
      const px = x + Math.cos(v.a) * k.r * v.m, py = y + Math.sin(v.a) * k.r * v.m;
      i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
    });
    ctx.closePath();
    ctx.fillStyle = '#1b2440'; ctx.fill();
    ctx.strokeStyle = 'rgba(140,160,220,0.28)'; ctx.lineWidth = 2; ctx.stroke();
  }
}
// aura rings (player request: "where IS my aura?") — every always-on zone around the player gets a
// faint fill + dashed edge at its TRUE radius (the same stats the tick logic reads, so it can't lie)
export function drawAuras() {
  if (S.dead) return;
  const x = player.x - cam.x, y = player.y - cam.y;
  const pulse = 0.5 + 0.5 * Math.sin(S.frame * 0.05);
  const rings = [];
  if (S.BURN_AURA > 0) rings.push({ r: S.BURN_AURA_R, col: '255,122,48' });    // Inferno Aura
  if (S.SLOW_AURA > 0) rings.push({ r: S.SLOW_AURA, col: '143,216,255' });     // Frost Field
  if (S.AEGIS_R > 0)   rings.push({ r: S.AEGIS_R, col: '189,199,206' });       // Aegis
  if (!rings.length) return;
  ctx.save();
  for (const a of rings) {
    ctx.fillStyle = `rgba(${a.col},${0.025 + 0.015 * pulse})`;
    ctx.beginPath(); ctx.arc(x, y, a.r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = `rgba(${a.col},${0.18 + 0.1 * pulse})`; ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 9]); ctx.lineDashOffset = -S.frame * 0.3;            // slow crawl so it reads as alive, not as a wall
    ctx.beginPath(); ctx.arc(x, y, a.r, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.restore();
}
export function drawWorldEdge() {   // the void's edge: same visual language as the boss ring, but violet — and blob-shaped
  if (Math.hypot(player.x, player.y) < worldLim(player.x, player.y) - Math.max(S.W, S.H) * 1.3) return;   // edge nowhere near the viewport
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i <= 96; i++) {
    const a = i / 96 * Math.PI * 2, r = worldLimA(a);
    const px = Math.cos(a) * r - cam.x, py = Math.sin(a) * r - cam.y;
    i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
  }
  ctx.closePath();
  ctx.strokeStyle = `rgba(140,100,255,${0.10 + 0.07 * Math.sin(S.frame * 0.05)})`; ctx.lineWidth = 36; ctx.stroke();   // soft outer glow
  ctx.strokeStyle = 'rgba(150,110,255,0.55)'; ctx.lineWidth = 5; ctx.stroke();
  ctx.restore();
}
